import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Form } from 'mobx-react-form';
import { toJS } from 'mobx';

import {
  Box,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { get, isEmpty, mergeWith } from 'lodash';

import { calculateEntrepreneurialProfitTotal, getDatesOfFinalValues, getFinalValues } from '../calculations';
import { useRootStore } from 'stores/RootStore';
import { formatCurrencyFloat } from 'shared/utils/numberFormatters';
import { Adjustment, LossItem, RentLossItem } from 'types/underwriting/income-approach/cap-rate-conclusion';

import { ADJUSTMENT_TYPES, ADJUSTMENT_TYPES_MAP, RENT_LOSS_LABELS } from '../constants';
import IncomeAdjustmentRow, { getInputStyles } from './IncomeAdjustmentRow';
import { CommercialRentRollUnit, ResidentialRentRollUnit } from 'types/underwriting/income-approach';
import { getRentLossPath, getRentLossRowTitle } from '../helpers';
import RentLossTableRow from './RentLossTableRow';
import { ConclusionType, RentLossType } from '../types';
import NumericInput from 'components/shared/inputs/NumericInput';
import DiscountRateRows from './DiscountRateRows';
import { incomeApproachCalculations } from 'shared/calculations/incomeApproach';
import { UnderwritingOutput } from 'types/underwriting/underwriting';
import LossItemTableRow from './LossItemTableRow';

type AdjustmentsAndLossesTableViewProps = {
  form: Form;
  valueAsComplete: boolean;
  valueAsStabilized: boolean;
  onEditRentLoss: (index: number, rentLossType: RentLossType, conclusionType: ConclusionType) => void;
  onRemoveRentLoss: (index: number, rentLossType: RentLossType, conclusionType: ConclusionType) => void;
};

const AdjustmentsAndLossesTableView = observer(
  ({
    form,
    valueAsComplete,
    valueAsStabilized,
    onEditRentLoss,
    onRemoveRentLoss,
  }: AdjustmentsAndLossesTableViewProps) => {
    const { underwritingPageStore } = useRootStore();
    const { underwritingData } = underwritingPageStore;

    const commercialRentRoll = get(
      underwritingPageStore.underwritingData,
      'incomeApproach.commercial.rentRoll',
      []
    ) as CommercialRentRollUnit[];
    const residentialRentRoll = get(
      underwritingPageStore.underwritingData,
      'incomeApproach.residential.rentRoll',
      []
    ) as ResidentialRentRollUnit[];

    const formValues = form.values();

    const npvAdjustments = get(formValues, 'incomeApproach.capRateConclusion.npvAdjustments', []) as Adjustment[];
    const asCompleteNpvAdjustments = get(
      formValues,
      'incomeApproach.capRateConclusion.asCompleteNpvAdjustments',
      []
    ) as Adjustment[];
    const asStabilizedNpvAdjustments = get(
      formValues,
      'incomeApproach.capRateConclusion.asStabilizedNpvAdjustments',
      []
    ) as Adjustment[];

    const asStabilizedResidentialRentLossItems = get(
      formValues,
      getRentLossPath('residential', 'asStabilized'),
      []
    ) as RentLossItem[];

    const asCompleteResidentialRentLossItems = get(
      formValues,
      getRentLossPath('residential', 'asComplete'),
      []
    ) as RentLossItem[];

    const asStabilizedCommercialRentLossItems = get(
      formValues,
      getRentLossPath('commercial', 'asStabilized'),
      []
    ) as RentLossItem[];

    const asCompleteCommercialRentLossItems = get(
      formValues,
      getRentLossPath('commercial', 'asComplete'),
      []
    ) as RentLossItem[];

    const asStabilizedLossItems = get(
      formValues,
      'incomeApproach.capRateConclusion.asStabilizedLossItems',
      []
    ) as LossItem[];

    const asCompleteLossItems = get(
      formValues,
      'incomeApproach.capRateConclusion.asCompleteLossItems',
      []
    ) as LossItem[];

    const includeAsStabilizedDiscountRate = get(
      formValues,
      'incomeApproach.capRateConclusion.includeAsStabilizedDiscountRate',
      false
    );
    const includeAsCompleteDiscountRate = get(
      formValues,
      'incomeApproach.capRateConclusion.includeAsCompleteDiscountRate',
      false
    );

    const removeAdjustmentOfType = (adjustmentType: string, index: number) => {
      const target = ADJUSTMENT_TYPES_MAP[adjustmentType];
      if (!target) {
        return;
      }

      try {
        const targetField = form.$(`incomeApproach.capRateConclusion.${target}`);
        const newFieldValue = (targetField.value || []).filter((_: any, i: number) => i !== index);
        targetField.set(newFieldValue);
        targetField.del(newFieldValue.length);
      } catch (error) {
        console.error('Error removing adjustment', error);
      }
    };

    const netOperatingIncome = useMemo(
      () => incomeApproachCalculations.calculateNetOperatingIncome(underwritingData || ({} as UnderwritingOutput)),
      [underwritingData]
    );

    const {
      capitalizationConclusion,
      capitalizationConclusionRounded,
      finalValueAsStabilized,
      finalValueAsStabilizedRounded,
      finalValueAsStabilizedPerUnit,
      finalValueAsStabilizedPerSf,
      finalValueAsComplete,
      finalValueAsCompleteRounded,
      finalValueAsCompletePerUnit,
      finalValueAsCompletePerSf,
      finalValueAsIs,
      finalValueAsIsRounded,
      finalValueAsIsPerUnit,
      finalValueAsIsPerSf,
      asCompleteDiscountRate,
      asStabilizedDiscountRate,
      asCompleteMarketValueWithDiscount,
      asStabilizedMarketValueWithDiscount,

      dateOfFinalValueAsIs,
      dateOfFinalValueAsComplete,
      dateOfFinalValueAsStabilized,

      asStabilizedEpTotal,
      asCompleteEpTotal,
    } = useMemo(() => {
      const mergedData = mergeWith({}, toJS(underwritingData), formValues, (objValue, srcValue) => {
        if (Array.isArray(objValue)) {
          return srcValue;
        }
      });

      const asStabilizedEpTotal = calculateEntrepreneurialProfitTotal(
        asStabilizedLossItems,
        [...asStabilizedResidentialRentLossItems, ...asStabilizedCommercialRentLossItems],
        asStabilizedNpvAdjustments
      );
      const asCompleteEpTotal = calculateEntrepreneurialProfitTotal(
        asCompleteLossItems,
        [...asCompleteResidentialRentLossItems, ...asCompleteCommercialRentLossItems],
        asCompleteNpvAdjustments
      );

      return {
        ...getFinalValues(mergedData),
        ...getDatesOfFinalValues(mergedData),
        asStabilizedEpTotal,
        asCompleteEpTotal,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues]);

    return (
      <Box>
        <Typography variant="subtitle1">
          <strong>Cap Rate Conclusion Summary Table</strong>
        </Typography>
        <Table>
          <TableHead sx={{ '& .MuiTableCell-head': { fontSize: 12, fontWeight: 700 } }}>
            <TableRow>
              <TableCell>Value</TableCell>
              <TableCell>Time Period</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right">Final Value (RD)</TableCell>
              <TableCell sx={{ width: 130 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Net Operating Income (NOI)</TableCell>
              <TableCell />
              <TableCell align="right">{formatCurrencyFloat(netOperatingIncome)}</TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell>Concluded Cap Rate</TableCell>
              <TableCell />
              <TableCell align="right">
                <NumericInput
                  {...form.$('incomeApproach.capRateConclusion.capRate').bind()}
                  sx={{ ...getInputStyles(true) }}
                  onFocus={(event) => event.target?.select()}
                  allowNegative={false}
                  autoComplete="off"
                  decimalScale={2}
                  fullWidth={false}
                  margin="none"
                  placeholder="0.00"
                  InputProps={{
                    sx: { paddingRight: 0 },
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </TableCell>
              <TableCell />
              <TableCell />
            </TableRow>

            {!isEmpty(npvAdjustments) && (
              <TableRow>
                <TableCell>Capitalization Conclusion</TableCell>
                <TableCell />
                <TableCell align="right">{capitalizationConclusion}</TableCell>
                <TableCell align="right">{capitalizationConclusionRounded}</TableCell>
                <TableCell />
              </TableRow>
            )}

            {npvAdjustments.map((adjustment, index) => (
              <IncomeAdjustmentRow
                form={form}
                fieldPath={`incomeApproach.capRateConclusion.npvAdjustments.${index}`}
                adjustment={adjustment}
                onRemove={() => removeAdjustmentOfType(ADJUSTMENT_TYPES.NPV, index)}
              />
            ))}

            {valueAsStabilized && (
              <React.Fragment>
                <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 600 }, bgcolor: 'grey.200' }}>
                  <TableCell>Prospective Market Value As Stabilized</TableCell>
                  <TableCell>{dateOfFinalValueAsStabilized}</TableCell>
                  <TableCell align="right">{finalValueAsStabilized}</TableCell>
                  <TableCell align="right">{finalValueAsStabilizedRounded}</TableCell>
                  <TableCell />
                </TableRow>
                <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 600 }, bgcolor: 'grey.200' }}>
                  <TableCell>Prospective Market Value As Stabilized Per Unit</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell align="right">{finalValueAsStabilizedPerUnit}</TableCell>
                  <TableCell />
                </TableRow>
                <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 600 }, bgcolor: 'grey.200' }}>
                  <TableCell>Prospective Market Value As Stabilized Per SF</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell align="right">{finalValueAsStabilizedPerSf}</TableCell>
                  <TableCell />
                </TableRow>
                {includeAsStabilizedDiscountRate && (
                  <DiscountRateRows
                    form={form}
                    type="asStabilized"
                    discountRate={asStabilizedDiscountRate}
                    marketValueWithDiscount={asStabilizedMarketValueWithDiscount}
                  />
                )}

                {asStabilizedNpvAdjustments.map((adjustment, index) => (
                  <IncomeAdjustmentRow
                    form={form}
                    fieldPath={`incomeApproach.capRateConclusion.asStabilizedNpvAdjustments.${index}`}
                    adjustment={adjustment}
                    onRemove={() => removeAdjustmentOfType(ADJUSTMENT_TYPES.AS_STABILIZED, index)}
                  />
                ))}

                {asStabilizedResidentialRentLossItems.map((rentLossItem, index) => (
                  <RentLossTableRow
                    form={form}
                    fieldPath={`${getRentLossPath('residential', 'asStabilized')}.${index}`}
                    rowTitle={getRentLossRowTitle(rentLossItem, residentialRentRoll, RENT_LOSS_LABELS.RESIDENTIAL)}
                    lossItem={rentLossItem}
                    onEditItem={() => onEditRentLoss(index, 'residential', 'asStabilized')}
                    onRemoveItem={() => onRemoveRentLoss(index, 'residential', 'asStabilized')}
                  />
                ))}

                {asStabilizedCommercialRentLossItems.map((rentLossItem, index) => (
                  <RentLossTableRow
                    form={form}
                    fieldPath={`${getRentLossPath('commercial', 'asStabilized')}.${index}`}
                    rowTitle={getRentLossRowTitle(rentLossItem, commercialRentRoll, RENT_LOSS_LABELS.COMMERCIAL)}
                    lossItem={rentLossItem}
                    onEditItem={() => onEditRentLoss(index, 'commercial', 'asStabilized')}
                    onRemoveItem={() => onRemoveRentLoss(index, 'commercial', 'asStabilized')}
                  />
                ))}

                {asStabilizedLossItems.map((lossItem, index) => (
                  <LossItemTableRow
                    form={form}
                    fieldPath={`incomeApproach.capRateConclusion.asStabilizedLossItems.${index}`}
                    lossItem={lossItem}
                    entrepreneurialProfitTotal={asStabilizedEpTotal}
                  />
                ))}
              </React.Fragment>
            )}
            {valueAsComplete && (
              <>
                <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 600 }, bgcolor: 'grey.200' }}>
                  <TableCell>Prospective Market Value As Complete</TableCell>
                  <TableCell>{dateOfFinalValueAsComplete}</TableCell>
                  <TableCell align="right">{finalValueAsComplete}</TableCell>
                  <TableCell align="right">{finalValueAsCompleteRounded}</TableCell>
                  <TableCell />
                </TableRow>
                <>
                  <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 600 }, bgcolor: 'grey.200' }}>
                    <TableCell>Prospective Market Value As Complete Per Unit</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell align="right">{finalValueAsCompletePerUnit}</TableCell>
                    <TableCell />
                  </TableRow>
                  <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 600 }, bgcolor: 'grey.200' }}>
                    <TableCell>Prospective Market Value As Complete Per SF</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell align="right">{finalValueAsCompletePerSf}</TableCell>
                    <TableCell />
                  </TableRow>
                </>
                {includeAsCompleteDiscountRate && (
                  <DiscountRateRows
                    form={form}
                    type="asComplete"
                    discountRate={asCompleteDiscountRate}
                    marketValueWithDiscount={asCompleteMarketValueWithDiscount}
                  />
                )}
                {asCompleteNpvAdjustments.map((adjustment, index) => (
                  <IncomeAdjustmentRow
                    form={form}
                    fieldPath={`incomeApproach.capRateConclusion.asCompleteNpvAdjustments.${index}`}
                    adjustment={adjustment}
                    onRemove={() => removeAdjustmentOfType(ADJUSTMENT_TYPES.AS_COMPLETE, index)}
                  />
                ))}

                {asCompleteResidentialRentLossItems.map((rentLossItem, index) => (
                  <RentLossTableRow
                    form={form}
                    fieldPath={`${getRentLossPath('residential', 'asComplete')}.${index}`}
                    rowTitle={getRentLossRowTitle(rentLossItem, residentialRentRoll, RENT_LOSS_LABELS.RESIDENTIAL)}
                    lossItem={rentLossItem}
                    onEditItem={() => onEditRentLoss(index, 'residential', 'asComplete')}
                    onRemoveItem={() => onRemoveRentLoss(index, 'residential', 'asComplete')}
                  />
                ))}

                {asCompleteCommercialRentLossItems.map((rentLossItem, index) => (
                  <RentLossTableRow
                    form={form}
                    fieldPath={`${getRentLossPath('commercial', 'asComplete')}.${index}`}
                    rowTitle={getRentLossRowTitle(rentLossItem, commercialRentRoll, RENT_LOSS_LABELS.COMMERCIAL)}
                    lossItem={rentLossItem}
                    onEditItem={() => onEditRentLoss(index, 'commercial', 'asComplete')}
                    onRemoveItem={() => onRemoveRentLoss(index, 'commercial', 'asComplete')}
                  />
                ))}

                {asCompleteLossItems.map((lossItem, index) => (
                  <LossItemTableRow
                    form={form}
                    fieldPath={`incomeApproach.capRateConclusion.asCompleteLossItems.${index}`}
                    lossItem={lossItem}
                    entrepreneurialProfitTotal={asCompleteEpTotal}
                  />
                ))}
              </>
            )}
          </TableBody>
          <TableFooter
            sx={{
              '& .MuiTableCell-root': { bgcolor: 'grey.200', color: 'text.primary', fontSize: 14, fontWeight: 600 },
            }}
          >
            <TableRow>
              <TableCell>As Is Market Value:</TableCell>
              <TableCell>{dateOfFinalValueAsIs}</TableCell>
              <TableCell align="right">{finalValueAsIs}</TableCell>
              <TableCell align="right">{finalValueAsIsRounded}</TableCell>
              <TableCell />
            </TableRow>
            <>
              <TableRow>
                <TableCell>As Is Market Value Per Unit</TableCell>
                <TableCell />
                <TableCell />
                <TableCell align="right">{finalValueAsIsPerUnit}</TableCell>
                <TableCell />
              </TableRow>
              <TableRow>
                <TableCell>As Is Market Value Per SF</TableCell>
                <TableCell />
                <TableCell />
                <TableCell align="right">{finalValueAsIsPerSf}</TableCell>
                <TableCell />
              </TableRow>
            </>
          </TableFooter>
        </Table>
      </Box>
    );
  }
);

export default AdjustmentsAndLossesTableView;
